import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import type { ToolUIPart } from 'ai';

import { Box, Flex, Icons, Text, Theme, type IconComponent } from 'baukasten';

import messages from './messages';
import type { ThinkingStep } from '../types';
import { formatDuration } from '../utils/formatDuration';

const Card = styled(Box)`
  border: 1px solid ${Theme.getColor('grey-light-100')};
  border-radius: 8px;
  overflow: hidden;
`;

type StatusStyle = {
  label: (typeof messages)[keyof typeof messages];
  icon: IconComponent;
  color: 'grey-dark' | 'green-dark' | 'red-dark';
};

const PENDING: StatusStyle = {
  label: messages.toolPending,
  icon: Icons.Pending,
  color: 'grey-dark',
};

const RUNNING: StatusStyle = {
  label: messages.toolRunning,
  icon: Icons.InProgress,
  color: 'grey-dark',
};

const FAILED: StatusStyle = {
  label: messages.toolFailed,
  icon: Icons.Failed,
  color: 'red-dark',
};

const STATUS: Record<ToolUIPart['state'], StatusStyle> = {
  'input-streaming': PENDING,
  'input-available': RUNNING,
  'approval-requested': PENDING,
  'approval-responded': RUNNING,
  'output-available': {
    label: messages.toolCompleted,
    icon: Icons.Done,
    color: 'green-dark',
  },
  'output-error': FAILED,
  'output-denied': FAILED,
};

type Props = { step: ThinkingStep; duration: number | undefined };

export const ToolStep = ({ step, duration }: Props) => {
  const { formatMessage } = useIntl();
  const status = STATUS[step.state];
  const StatusIcon = status.icon;

  return (
    <Card p={2}>
      <Flex gap={2} alignItems="center">
        <StatusIcon color={status.color} size="14px" noPadding />
        <Text fontSize={12} fontWeight="semibold">
          {step.label}
        </Text>
        <Text fontSize={12} color={status.color}>
          {formatMessage(status.label)}
        </Text>
        {duration !== undefined && (
          <Text fontSize={12} color="grey-dark">
            {formatDuration(duration)}
          </Text>
        )}
      </Flex>
    </Card>
  );
};
